import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Divider } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../service';
import { useCurrency, formatCurrency } from '../../../utils/currency';

export default function RevenueBreakdownChart({ initialData }) {
    const navigate = useNavigate();
    const { currency } = useCurrency();
    const [chartData, setChartData] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [loading, setLoading] = useState(false);

    const processApiData = (res) => {
        if (!res) {
            setChartData([]);
            setTotalRevenue(0);
            return;
        }

        let rawCategories = [];
        if (res.categories && Array.isArray(res.categories)) {
            rawCategories = res.categories;
        } else if (res.channels && Array.isArray(res.channels)) {
            rawCategories = res.channels;
        } else if (res.breakdown && Array.isArray(res.breakdown)) {
            rawCategories = res.breakdown;
        } else if (Array.isArray(res)) {
            rawCategories = res;
        }

        let tot = Number(res.totalRevenue ?? res.totalAmount ?? 0);
        if (tot === 0 && rawCategories.length > 0) {
            tot = rawCategories.reduce((acc, curr) => acc + (Number(curr.amount || curr.value || curr.total || 0)), 0);
        }
        setTotalRevenue(tot);

        if (rawCategories.length > 0) {
            const colors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'];
            const mapped = rawCategories.slice(0, 4).map((item, idx) => {
                const amt = Number(item.amount ?? item.value ?? item.total ?? 0);
                const pct = item.percent !== undefined 
                    ? Number(item.percent) 
                    : (tot > 0 ? Math.round((amt / tot) * 100) : 0);
                return {
                    label: item.label || item.category || item.name || `Channel ${idx + 1}`,
                    percent: pct,
                    amount: amt,
                    formattedAmount: formatCurrency(amt, currency),
                    color: item.color || colors[idx % colors.length]
                };
            });
            const sumPct = mapped.reduce((acc, c) => acc + c.percent, 0);
            if (sumPct > 0 && sumPct !== 100 && mapped.length > 0) {
                mapped[0].percent += (100 - sumPct);
            }
            setChartData(mapped);
        } else {
            setChartData([]);
        }
    };

    const fetchChartData = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const res = await DashboardService.fetchRevenueBreakdown(tenantId, { period: 'this_month' });
            processApiData(res);
        } catch (err) {
            console.error('Failed to fetch revenue channels breakdown:', err);
            setChartData([]);
            setTotalRevenue(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialData) {
            processApiData(initialData);
        } else {
            fetchChartData();
        }
    }, [initialData]);

    const channels = chartData;

    // SVG Donut Calculations
    const size = 150;
    const strokeWidth = 14;
    const center = size / 2;
    const radius = center - strokeWidth;
    const circumference = 2 * Math.PI * radius;

    let cumulativePercent = 0;

    const displayTotal = totalRevenue > 0 && totalRevenue >= 100000 && currency === 'INR'
        ? `₹${(totalRevenue / 100000).toFixed(2)}L`
        : formatCurrency(totalRevenue, currency);

    return (
        <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
            border: '1px solid #f1f5f9',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }}>
            <CardContent sx={{ p: 2.5, pb: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                        Revenue Channels
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                        This Month
                    </Typography>
                </Box>

                {/* Donut Chart with Center Total */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 1, position: 'relative' }}>
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                        {/* Background track circle */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="transparent"
                            stroke="#f1f5f9"
                            strokeWidth={strokeWidth}
                        />

                        {/* Segment Arcs */}
                        {channels.map((chan, idx) => {
                            const strokeDasharray = `${(chan.percent / 100) * circumference} ${circumference}`;
                            const strokeDashoffset = -((cumulativePercent / 100) * circumference);
                            cumulativePercent += chan.percent;

                            return (
                                <circle
                                    key={idx}
                                    cx={center}
                                    cy={center}
                                    r={radius}
                                    fill="transparent"
                                    stroke={chan.color}
                                    strokeWidth={strokeWidth}
                                    strokeDasharray={strokeDasharray}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dasharray 0.5s ease' }}
                                />
                            );
                        })}
                    </svg>

                    {/* Donut Hole Center Text */}
                    <Box sx={{ 
                        position: 'absolute', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        pointerEvents: 'none'
                    }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                            TOTAL
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                            {displayTotal}
                        </Typography>
                    </Box>
                </Box>

                {/* Channel Breakdown List or Empty State */}
                {channels.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 2, mb: 1.5 }}>
                        {channels.map((chan, idx) => (
                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: chan.color }} />
                                    <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.78rem', fontWeight: 500 }}>
                                        {chan.label}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                                        {chan.percent}%
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                        {chan.formattedAmount}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        py: 2.5, 
                        px: 1, 
                        textAlign: 'center',
                        mt: 1
                    }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: 600 }}>
                            No channel revenue recorded
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', mt: 0.5 }}>
                            Breakdown will populate as bookings arrive across channels.
                        </Typography>
                    </Box>
                )}
            </CardContent>

            {/* Bottom Footer Link */}
            <Box>
                <Divider sx={{ borderColor: '#f1f5f9' }} />
                <Box 
                    onClick={() => navigate('/ota')}
                    sx={{ 
                        p: 1.5, 
                        px: 2.5,
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: '#64748b',
                        transition: 'all 0.2s',
                        '&:hover': { color: 'primary.main', bgcolor: 'rgba(99, 102, 241, 0.02)' }
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        Channel Matrix
                    </Typography>
                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                </Box>
            </Box>
        </Card>
    );
}
